import { fromJS, List } from 'immutable'
import { keyBy } from 'lodash'
import * as actionTypes from './constants'
import {
  UPDATE_PINS_BATCH,
  UPDATE_PINS_BATCH_DONE,
  UPDATE_PINS_BATCH_ERROR
} from '../PlanViewerNew/constants'
import { LOGOUT } from '../authentication/constants'
import { CLEAR_PROJECT_DETAILS } from '../ProjectSettings/constants'
import { setTaskStates } from '../../utils/setTaskState'
import { DateTime } from 'luxon'

const initialState = fromJS({
  pendingSetToInactive: {},
  pendingSetToInactiveErrors: {},
  pendingSetToActive: {},
  extraPinPanToTop: 0,
  updatingPostIds: List(),
  isOpenAlertPlanViewer: fromJS({
    isOpen: false,
    variant: '',
    title: '',
    description: '',
    actionButtons: []
  })
})

function planReducer(state = initialState, action) {
  let newState = state

  switch (action.type) {
    case actionTypes.GET_PIN:
      if (action.pin) {
        newState = newState.set('pin', fromJS(action.pin))
      } else if (!action.noReaload) {
        newState = newState.delete('pin')
      }
      return newState.set('gettingPin', true).delete('getPinError')
    case actionTypes.GET_PIN_DONE:
      return state.set('gettingPin', false).set('pin', fromJS(action.pin))
    case actionTypes.GET_PIN_ERROR:
      return state.set('gettingPin', false).set('getPinError', action.error)

    case actionTypes.CLEAR_PIN:
      return state.delete('pin')

    // getLastPinTitles
    case actionTypes.GET_LAST_PIN_TITLES:
      if (action.projectId !== newState.get('pinTitlesProjectId')) {
        newState = newState.delete('lastPinTitles')
      }
      return newState
        .set('gettingLastPinTitles', true)
        .set('pinTitlesProjectId', action.projectId)
    case actionTypes.GET_LAST_PIN_TITLES_DONE:
      return state
        .set('gettingLastPinTitles', false)
        .set('lastPinTitles', action.pinTitles)
    case actionTypes.GET_LAST_PIN_TITLES_ERROR:
      return state
        .set('gettingLastPinTitles', false)
        .set('getLastPinTitlesError', action.error)
    // getLastTaskTitles
    case actionTypes.GET_LAST_TASK_TITLES:
      if (action.projectId !== newState.get('taskTitlesProjectId')) {
        newState = newState.delete('lastTaskTitles')
      }
      return newState
        .set('gettingLastTaskTitles', true)
        .set('taskTitlesProjectId', action.projectId)
    case actionTypes.GET_LAST_TASK_TITLES_DONE:
      return state
        .set('gettingLastTaskTitles', false)
        .set('lastTaskTitles', action.taskTitles)
    case actionTypes.GET_LAST_TASK_TITLES_ERROR:
      return state
        .set('gettingLastTaskTitles', false)
        .set('getLastTaskTitlesError', action.error)

    case actionTypes.UPDATE_PIN: {
      const pin = state.get('pin')
      const updatedState = state
        .set('updatingPin', true)
        .delete('updatePinError')
      if (pin && action.pin.id === pin.get('id')) {
        const { fieldValues = {}, ...rest } = action.pin
        const merged = pin
          .mergeDeep(fromJS(rest))
          .mergeIn(['fieldValues'], fromJS(fieldValues))
        return updatedState.set('pin', merged).set('oldPin', pin)
      }
      return updatedState
    }
    case actionTypes.UPDATE_PIN_DONE:
      if (action.pin.id === state.getIn(['pin', 'id'])) {
        newState = state.set('pin', fromJS(action.pin))
        if (!action.fromSync) return newState.set('updatingPin', false)
        return newState
      }
      return state

    case actionTypes.UPDATE_PIN_ERROR:
      return state
        .set('updatingPin', false)
        .set('updatePinError', action.error)
        .set('pin', state.get('oldPin'))
    case actionTypes.REMOVE_UPDATE_PIN_ERROR:
      return state.delete('updatePinError')

    case actionTypes.SUGGEST_TAGS:
      return state
        .set('suggestingTags', true)
        .delete('suggestedTags')
        .delete('suggestTagsError')
    case actionTypes.SUGGEST_TAGS_DONE:
      return state
        .set('suggestingTags', false)
        .set('suggestedTags', action.suggestedTags)
    case actionTypes.SUGGEST_TAGS_ERROR:
      return state
        .set('suggestingTags', false)
        .set('suggestTagsError', action.error)

    case actionTypes.GET_TASK_LIST:
      return state
        .set('gettingTaskList', true)
        .set('taskListSortOptions', {
          sort: action.sort,
          sortDirection: action.sortDirection
        })
        .set('taskListOptions', {
          stateFilter: action.stateFilter,
          creatorFilter: action.creatorFilter,
          from: action.from,
          to: action.to
        })
        .delete('getTaskListError')
    case actionTypes.GET_TASK_LIST_DONE: {
      let tasks = state.get('taskList')
      // coming from SW is an array non paginated.
      let newTasks = []
      if (Array.isArray(action.tasks)) {
        newTasks = fromJS(action.tasks.map(setTaskStates))
      } else {
        newTasks = fromJS(action.tasks.content.map(setTaskStates))
      }
      if (!action.tasks.number) {
        tasks = newTasks
      } else {
        tasks = tasks.concat(newTasks)
      }
      return state
        .set('gettingTaskList', false)
        .set('taskList', tasks)
        .set('taskListCurrentPage', action.tasks.number)
        .set('taskListTotalPages', action.tasks.totalPages)
    }
    case actionTypes.GET_TASK_LIST_ERROR:
      return state
        .set('gettingTaskList', false)
        .set('getTaskListError', action.error)

    case actionTypes.GET_POST_LIST:
      return state
        .set('gettingPostList', true)
        .set('postListSortOptions', {
          sort: action.sort,
          sortDirection: action.sortDirection
        })
        .set('postListOptions', {
          filter: action.filter,
          showInactive: action.showInactive,
          creatorFilter: action.creatorFilter,
          from: action.from,
          to: action.to
        })
        .delete('postList')
        .delete('getPostListError')
    case actionTypes.GET_POST_LIST_DONE:
      return state
        .set('gettingPostList', false)
        .set('postList', fromJS(action.posts))
    case actionTypes.GET_POST_LIST_ERROR:
      return state
        .set('gettingPostList', false)
        .set('getPostListError', action.error)
    case actionTypes.SET_OPEN_POST_ID:
      return state
        .set('openPostId', action.postId)
        .set('openListPostId', action.postId)
    case actionTypes.REMOVE_OPEN_LIST_POST_ID:
      return state.delete('openPostId', action).delete('openListPostId', action)

    case actionTypes.GET_COMMENTS_OF_POST:
      return state
        .set('gettingCommentsOfPost', true)
        .delete('commentsOfPost')
        .delete('getCommentsOfPostError')
    case actionTypes.GET_COMMENTS_OF_POST_DONE:
      return state
        .set('gettingCommentsOfPost', false)
        .set('commentsOfPost', fromJS(action.comments))
    case actionTypes.CLEAR_COMMENTS:
      return state.delete('commentsOfPost')
    case actionTypes.GET_COMMENTS_OF_POST_ERROR:
      return state
        .set('gettingCommentsOfPost', false)
        .set('getCommentsOfPostError', action.error)

    case actionTypes.CREATE_COMMENT:
      return state
        .set('creatingCommentForPost', true)
        .delete('createCommentError')
    case actionTypes.CREATE_COMMENT_DONE: {
      newState = newState.has('commentsOfPost')
        ? newState.set(
            'commentsOfPost',
            newState.get('commentsOfPost').unshift(action.comment)
          )
        : newState.set('commentsOfPost', fromJS([action.comment]))
      newState = newState.set(
        'taskList',
        newState.get('taskList').update(
          newState
            .get('taskList')
            .findIndex((p) => p.get('id') === newState.get('openPostId')),
          (p) => {
            if (p) {
              return p.set('commentCount', p.get('commentCount') + 1)
            }
            return p
          }
        )
      )

      newState = newState.set(
        'postList',
        newState.get('postList').update(
          newState
            .get('postList')
            .findIndex((p) => p.get('id') === newState.get('openPostId')),
          (p) => {
            if (p) {
              return p.set('commentCount', p.get('commentCount') + 1)
            }
            return p
          }
        )
      )

      return newState.set('creatingCommentForPost', false)
    }
    case actionTypes.CREATE_COMMENT_ERROR:
      return state
        .set('creatingCommentForPost', false)
        .set('createCommentError', action.error)
    case actionTypes.UPDATE_COMMENT_DONE: {
      const updatedCommentIndex = newState
        .get('commentsOfPost')
        .findIndex((comment) => comment && comment.id === action.comment.id)

      newState = newState.setIn(
        ['commentsOfPost', updatedCommentIndex],
        fromJS(action.comment)
      )
      return newState.set('updatingCommentForPost', false)
    }
    case actionTypes.UPDATE_COMMENT_ERROR:
      return state
        .set('updatingCommentForPost', false)
        .set('updateCommentError', action.error)

    case actionTypes.OPEN_FULL_SCREEN:
      return state.set('fullScreen', true)
    case actionTypes.CLOSE_FULL_SCREEN:
      return state.set('fullScreen', false)

    case actionTypes.UPDATE_TASK: {
      if (
        !newState
          .get('taskList')
          .some((task) => task.get('id') === action.task.id)
      ) {
        const newTask = {
          ...action.task,
          updating: true
        }
        newState = newState.updateIn(
          ['persistentTempTask', action.pinId],
          List(),
          (list) => list.push(fromJS(newTask))
        )
      }
      return newState.set('updatingTask', true).delete('updateTaskError')
    }
    case actionTypes.UPDATE_TASK_DONE: {
      const tempTasks = newState.getIn(['persistentTempTask', action.pinId])
      const updatedTask = setTaskStates(action.task)
      if (tempTasks) {
        const tempTaskIndex = tempTasks.findIndex(
          (task) => task.get('id') === action.task.id
        )
        if (tempTaskIndex >= 0) {
          if (
            newState.getIn(['persistentTempTask', action.pinId]).count() < 2
          ) {
            newState = newState.set(
              'persistentTempTask',
              newState.get('persistentTempTask').delete(action.pinId)
            )
          } else {
            newState = newState.updateIn(
              ['persistentTempTask', action.pinId],
              (list) => list.delete(tempTaskIndex)
            )
          }
        }
      }
      return newState.set('updatingTask', false).set(
        'taskList',
        newState.get('taskList')
          ? newState.get('taskList').set(
              newState
                .get('taskList')
                .findIndex((task) => task.get('id') === action.task.id),
              fromJS(updatedTask)
            )
          : undefined
      )
    }
    case actionTypes.UPDATE_TASK_ERROR: {
      const tempTasks = newState.getIn([
        'persistentTempTask',
        action.task.pinId
      ])
      if (tempTasks) {
        const tempIndex = tempTasks.findIndex(
          (task) => task.get('id') === action.task.id
        )
        if (tempIndex >= 0) {
          newState = newState.setIn(
            ['persistentTempTask', action.task.pinId, tempIndex],
            fromJS(action.task)
          )
        }
      }
      return newState
        .set('updatingTask', false)
        .set('updateTaskError', action.error)
    }

    // companyAssignees
    case actionTypes.GET_PROJECT_COMPANIES:
      if (action.projectId !== newState.get('companiesProjectId')) {
        newState = newState.delete('companies')
        newState = newState.delete('assignees')
      }
      return newState
        .set('gettingCompanies', true)
        .set('companiesProjectId', action.projectId)
    case actionTypes.GET_PROJECT_COMPANIES_DONE:
      return state
        .set('gettingCompanies', false)
        .set('companies', action.companies)
    case actionTypes.GET_PROJECT_COMPANIES_ERROR:
      return state
        .set('gettingCompanies', false)
        .set('getProjectCompaniesError', action.error)
    case actionTypes.GET_COMPANY_ASSIGNEES:
      return newState.set('gettingAssignees', true)
    case actionTypes.GET_COMPANY_ASSIGNEES_PROGRESS:
      // If we have pre-populated assignee list, we don't want to override it with incomplete intermediary results.
      return action.assignees.length > (state.get('assignees')?.length ?? 0)
        ? state.set('assignees', action.assignees)
        : state
    case actionTypes.GET_COMPANY_ASSIGNEES_DONE:
      return state
        .set('gettingAssignees', false)
        .set('assignees', action.assignees)
    case actionTypes.GET_COMPANY_ASSIGNEES_ERROR:
      return state
        .set('gettingAssignees', false)
        .set('getCompanyAssigneesError', action.error)

    case actionTypes.GET_PROJECT_DEPENDENCY_SUGGESTIONS:
      if (action.projectId !== newState.get('dependencySuggestionsProjectId')) {
        newState = newState.delete('projectDependencySuggestions')
      }
      return newState
        .set('gettingProjectDependencySuggestions', true)
        .set('dependencySuggestionsProjectId', action.projectId)
        .delete('getProjectDependencySuggestionsError')
    case actionTypes.GET_PROJECT_DEPENDENCY_SUGGESTIONS_DONE:
      return state
        .set('gettingProjectDependencySuggestions', false)
        .set('projectDependencySuggestions', action.dependencySuggestions)
    case actionTypes.GET_PROJECT_DEPENDENCY_SUGGESTIONS_ERROR:
      return state
        .set('gettingProjectDependencySuggestions', false)
        .set('getProjectDependencySuggestionsError', action.error)

    case actionTypes.GET_ISSUE_DEPENDENCY_SUGGESTIONS:
      if (action.pinId !== newState.get('dependencySuggestionsIssueId')) {
        newState = newState.delete('issueDependencySuggestions')
      }
      return newState
        .set('gettingIssueDependencySuggestions', true)
        .set('dependencySuggestionsIssueId', action.pinId)
        .delete('getIssueDependencySuggestionsError')
    case actionTypes.GET_ISSUE_DEPENDENCY_SUGGESTIONS_DONE:
      return state
        .set('gettingIssueDependencySuggestions', false)
        .set('issueDependencySuggestions', action.dependencySuggestions)
    case actionTypes.GET_ISSUE_DEPENDENCY_SUGGESTIONS_ERROR:
      return state
        .set('gettingIssueDependencySuggestions', false)
        .set('getIssueDependencySuggestionsError', action.error)

    case actionTypes.CREATE_TASK: {
      if (action.retries > 1) return newState
      const newTask = {
        ...action.task,
        tempId: action.tempId,
        created: DateTime.now().toISO()
      }
      newState = newState.updateIn(
        ['persistentTempTask', action.pinId],
        List(),
        (list) => list.push(fromJS(newTask))
      )
      return newState.set('creatingTask', true).delete('createTaskError')
    }
    case actionTypes.CREATE_TASK_DONE: {
      const newTask = setTaskStates(action.task)
      const tempTaskIndex = newState
        .getIn(['persistentTempTask', action.pinId])
        .findIndex((p) => p.get('tempId') === action.tempId)
      const taskListSortOptions = state.get('taskListSortOptions')
      if (tempTaskIndex >= 0) {
        if (newState.getIn(['persistentTempTask', action.pinId]).count() < 2) {
          newState = newState.set(
            'persistentTempTask',
            newState.get('persistentTempTask').delete(action.pinId)
          )
        } else {
          newState = newState.updateIn(
            ['persistentTempTask', action.pinId],
            (list) => list.delete(tempTaskIndex)
          )
        }
      }
      if (newState.get('taskList')) {
        if (
          taskListSortOptions.sort !==
          actionTypes.postListOptions.sort.createdBy
        ) {
          if (
            taskListSortOptions.sortDirection ===
            actionTypes.postListOptions.sortDirection.DESC
          ) {
            newState = newState.set(
              'taskList',
              newState.get('taskList').unshift(fromJS(newTask))
            )
          } else {
            newState = newState.set(
              'taskList',
              newState.get('taskList').push(fromJS(newTask))
            )
          }
        } else {
          newState = newState.set(
            'taskList',
            newState
              .get('taskList')
              .unshift(fromJS(newTask))
              .sortBy((t) => t.createdBy)
          )

          if (
            taskListSortOptions.sortDirection ===
            actionTypes.postListOptions.sortDirection.DESC
          ) {
            newState = newState.set(
              'taskList',
              newState.get('taskList').reverse()
            )
          }
        }
      } else {
        newState = newState.set('taskList', fromJS([newTask]))
      }
      return newState.set('creatingTask', false)
    }
    case actionTypes.CREATE_TASK_ERROR: {
      const tempIndex = newState
        .getIn(['persistentTempTask', action.task.pinId])
        .findIndex((task) => task.get('tempId') === action.task.tempId)
      if (tempIndex >= 0) {
        newState = newState.setIn(
          ['persistentTempTask', action.task.pinId, tempIndex],
          fromJS(action.task)
        )
      }
      return newState
        .set('creatingTask', false)
        .set('createTaskError', action.error)
    }
    case actionTypes.CLEAR_TASK_ERRORS:
      return state
        .delete('createTaskError', action.error)
        .delete('updateTaskError', action.error)

    case actionTypes.CREATE_POST: {
      if (action.retries > 1) return newState
      const newPost = {
        tempId: action.tempId,
        description: action.post.description,
        type: action.post.type,
        previews: action.previews,
        created: DateTime.now().toISO()
      }
      newState = newState.updateIn(
        ['persistentTempPost', action.pinId],
        List(),
        (list) => list.push(fromJS(newPost))
      )
      return newState.set('creatingPost', true).delete('createPostError')
    }
    case actionTypes.CREATE_POST_DONE: {
      const tempPostIndex = newState
        .getIn(['persistentTempPost', action.pinId])
        .findIndex((p) => p.get('tempId') === action.tempId)
      const postListSortOptions = state.get('postListSortOptions')

      if (tempPostIndex >= 0) {
        if (action.post.type === actionTypes.mediaTypes.VISUALS) {
          action.post.files.forEach((f) => {
            const fileName = newState
              .getIn([
                'persistentTempPost',
                action.pinId,
                tempPostIndex,
                'previews'
              ])
              .find((p) => p.name === f.fileName)
            if (fileName) {
              // eslint-disable-next-line no-param-reassign
              f.preview = fileName.get('preview')
            }
          })
        }
        if (newState.getIn(['persistentTempPost', action.pinId])?.count() < 2) {
          newState = newState.set(
            'persistentTempPost',
            newState.get('persistentTempPost').delete(action.pinId)
          )
        } else {
          newState = newState.updateIn(
            ['persistentTempPost', action.pinId],
            (list) => list.delete(tempPostIndex)
          )
        }
      }
      const post = fromJS(action.post)
      if (
        postListSortOptions.sort !== actionTypes.postListOptions.sort.createdBy
      ) {
        if (
          postListSortOptions.sortDirection ===
          actionTypes.postListOptions.sortDirection.DESC
        ) {
          newState = newState.set(
            'postList',
            newState.get('postList')?.unshift(post) ?? fromJS([post])
          )
        } else {
          newState = newState.set(
            'postList',
            newState.get('postList')?.push(post) ?? fromJS([post])
          )
        }
      } else {
        newState = newState.set(
          'postList',
          newState
            .get('postList')
            ?.unshift(post)
            .sortBy((t) => t.createdBy) ?? fromJS([post])
        )

        if (
          postListSortOptions.sortDirection ===
          actionTypes.postListOptions.sortDirection.DESC
        ) {
          newState = newState.set(
            'postList',
            newState.get('postList').reverse()
          )
        }
      }
      return newState.set('creatingPost', false)
    }
    case actionTypes.CREATE_POST_ERROR: {
      const tempIndex = newState
        .getIn(['persistentTempPost', action.post.pinId])
        .findIndex((p) => p.get('tempId') === action.post.tempId)
      if (tempIndex >= 0) {
        newState = newState.setIn(
          ['persistentTempPost', action.post.pinId, tempIndex],
          fromJS(action.post)
        )
      }
      return newState
        .set('creatingPost', false)
        .set('createPostError', action.error)
    }
    case actionTypes.UPDATE_POST: {
      const newPost = {
        ...action.post,
        filesToDelete: action.filesToDelete,
        newFiles: action.newFiles
      }
      newState = newState.updateIn(
        ['persistentTempPost', action.pinId],
        List(),
        (list) => list.push(fromJS(newPost))
      )
      return newState
        .set('updatingPost', true)
        .update('updatingPostIds', (list) => list.push(action.post.id))
        .delete('updatePostError')
    }
    case actionTypes.UPDATE_POST_DONE: {
      const tempPostIndex = newState
        .getIn(['persistentTempPost', action.pinId])
        .findIndex((p) => p.get('id') === action.post.id)
      if (tempPostIndex >= 0) {
        if (newState.getIn(['persistentTempPost', action.pinId]).count() < 2) {
          newState = newState.set(
            'persistentTempPost',
            newState.get('persistentTempPost').delete(action.pinId)
          )
        } else {
          newState = newState.updateIn(
            ['persistentTempPost', action.pinId],
            (list) => list.delete(tempPostIndex)
          )
        }
      }
      return newState
        .set('updatingPost', false)
        .update('updatingPostIds', (list) =>
          list.delete(
            newState
              .get('updatingPostIds')
              .findIndex((postId) => postId === action.post.id)
          )
        )
        .set(
          'postList',
          newState.has('postList')
            ? newState.get('postList').set(
                newState
                  .get('postList')
                  .findIndex((p) => p.get('id') === action.post.id),
                fromJS(action.post)
              )
            : undefined
        )
    }
    case actionTypes.UPDATE_POST_ERROR: {
      const tempIndex = newState
        .getIn(['persistentTempPost', action.post.pinId])
        .findIndex((p) => p.get('id') === action.post.id)
      if (tempIndex >= 0) {
        newState = newState.setIn(
          ['persistentTempPost', action.post.pinId, tempIndex],
          fromJS(action.post)
        )
      }
      return newState
        .set('updatingPost', false)
        .update('updatingPostIds', (list) =>
          list.delete(
            newState
              .get('updatingPostIds')
              .findIndex((postId) => postId === action.post.id)
          )
        )
        .set('updatePostError', action.error)
    }
    case actionTypes.CLEAR_POST_ERRORS:
      return state
        .delete('createPostError', action.error)
        .delete('updatePostError', action.error)

    case actionTypes.GET_RECENT_NOTES:
      if (action.projectId !== newState.get('recentNotesProjectId')) {
        newState = newState.delete('projectRecentNotes')
      }
      return newState
        .set('gettingProjectRecentNotes', true)
        .set('recentNotesProjectId', action.projectId)
        .delete('getRecentNotesError')
    case actionTypes.GET_RECENT_NOTES_DONE:
      return state
        .set('gettingProjectRecentNotes', false)
        .set('projectRecentNotes', action.recentNotes)
    case actionTypes.GET_RECENT_NOTES_ERROR:
      return state
        .set('gettingProjectRecentNotes', false)
        .set('getRecentNotesError', action.error)

    case actionTypes.GET_DATASET:
      if (state.get('datasetsProject') !== action.projectId) {
        newState.delete('datasets')
        newState.set('datasetsProject', action.projectId)
      }
      return newState.set('gettingDataset', true).delete('getDatasetError')
    case actionTypes.GET_DATASET_DONE:
      return state
        .set('gettingDataset', false)
        .setIn(['datasets', action.dataset.id], action.dataset)
    case actionTypes.GET_DATASET_ERROR:
      return state
        .set('gettingDataset', false)
        .set('getDatasetError', action.error)

    case actionTypes.GET_DATASET_RECORDS_PAGE:
      return newState
        .set('gettingDatasetRecordsPage', true)
        .delete('getDatasetRecordsPageError')
    case actionTypes.GET_DATASET_RECORDS_PAGE_DONE:
      return state
        .set('gettingDatasetRecordsPage', false)
        .setIn(
          ['datasetsRecords', action.datasetId, action.page],
          action.records
        )
    case actionTypes.GET_DATASET_RECORDS_PAGE_ERROR:
      return state
        .set('gettingDatasetRecordsPage', false)
        .set('getDatasetRecordsPageError', action.error)

    case actionTypes.GET_ALL_DATASETS:
      if (state.get('datasetsProject') !== action.projectId) {
        newState.delete('datasets')
        newState.set('datasetsProject', action.projectId)
      }
      return newState
        .set('gettingAllDatasets', true)
        .delete('getAllDatasetsError')
    case actionTypes.GET_ALL_DATASETS_DONE:
      return state
        .set('gettingAllDatasets', false)
        .set('datasets', fromJS(keyBy(action.datasets, 'id')))
    case actionTypes.GET_ALL_DATASETS_ERROR:
      return state
        .set('gettingAllDatasets', false)
        .set('getAllDatasetsError', action.error)

    case actionTypes.MOVE_PIN_POSITION:
      return state.set('movingPinPosition', true)
    case actionTypes.STOP_MOVE_PIN_POSITION:
      return state.set('movingPinPosition', false)

    case actionTypes.COPY_PIN:
      return state.set('copyingPin', true)
    case actionTypes.STOP_COPY_PIN:
      return state.set('copyingPin', false)

    case actionTypes.GET_ISSUE_HISTORY:
      return newState
        .set('gettingIssueHistory', true)
        .delete('getIssueHistoryError')
        .delete('history')
    case actionTypes.GET_ISSUE_HISTORY_DONE:
      return state
        .set('gettingIssueHistory', false)
        .set('history', action.history)
    case actionTypes.GET_ISSUE_HISTORY_ERROR:
      return state
        .set('gettingIssueHistory', false)
        .set('getIssueHistoryError', action.error)
    case actionTypes.GET_ISSUE_PROPERTY_HISTORY:
      return newState
        .set('gettingIssuePropertyHistory', true)
        .delete('getIssuePropertyHistoryError')
        .delete('propertyHistory')
    case actionTypes.GET_ISSUE_PROPERTY_HISTORY_DONE:
      return state
        .set('gettingIssuePropertyHistory', false)
        .set('propertyHistory', action.history)
    case actionTypes.GET_ISSUE_PROPERTY_HISTORY_ERROR:
      return state
        .set('gettingIssuePropertyHistory', false)
        .set('getIssuePropertyHistoryError', action.error)

    case actionTypes.GET_POST_HISTORY:
      return newState
        .set('gettingPostHistory', true)
        .delete('getPostHistoryError')
        .delete('postHistory')
    case actionTypes.GET_POST_HISTORY_DONE:
      return state
        .set('gettingPostHistory', false)
        .set('postHistory', action.history)
    case actionTypes.GET_POST_HISTORY_ERROR:
      return state
        .set('gettingPostHistory', false)
        .set('getPostHistoryError', action.error)

    case actionTypes.GET_ALL_HISTORIES:
      return newState
        .set('gettingAllHistories', true)
        .delete('getAllHistoriesError')
        .delete('allHistories')
    case actionTypes.GET_ALL_HISTORIES_DONE:
      return state
        .set('gettingAllHistories', false)
        .set('allHistories', action.history)
    case actionTypes.GET_ALL_HISTORIES_ERROR:
      return state
        .set('gettingAllHistories', false)
        .set('getAllHistoriesError', action.error)

    case UPDATE_PINS_BATCH:
      return state.set('updatingPin', true)
    case UPDATE_PINS_BATCH_DONE:
      if (state.get('pin')) {
        action.pins.forEach((p) => {
          if (p.id === state.get('pin').get('id')) {
            newState = newState.set('pin', fromJS(p))
          }
        })
      }
      return newState.set('updatingPin', false)
    case UPDATE_PINS_BATCH_ERROR:
      return state.set('updatingPin', false)

    case actionTypes.SET_MEDIA_INACTIVE:
      return state
        .setIn(['pendingSetToInactive', action.fileId], true)
        .deleteIn(['pendingSetToInactiveErrors', action.fileId])
    case actionTypes.SET_MEDIA_INACTIVE_DONE: {
      if (state.has('postList')) {
        const postIndex = state
          .get('postList')
          .findIndex((p) => p.get('id') === action.postId)
        let post = state.getIn(['postList', postIndex])

        const fileIndex = post
          .get('files')
          .findIndex((file) => file.get('id') === action.fileId)
        const file = post.getIn(['files', fileIndex])

        post = post
          .set('files', post.get('files').delete(fileIndex))
          .set('inactiveFiles', post.get('inactiveFiles').push(file))

        newState = state.setIn(['postList', postIndex], post)
      }

      return newState.deleteIn(['pendingSetToInactive', action.fileId])
    }
    case actionTypes.SET_MEDIA_INACTIVE_ERROR:
      return newState
        .deleteIn(['pendingSetToInactive', action.fileId])
        .deleteIn(['pendingSetToInactiveErrors', action.fileId], action.error)

    case actionTypes.SET_MEDIA_ACTIVE:
      return state
        .setIn(['pendingSetToActive', action.fileId], true)
        .deleteIn(['pendingSetToActiveErrors', action.fileId])
    case actionTypes.SET_MEDIA_ACTIVE_DONE: {
      if (state.has('postList')) {
        const postIndex = state
          .get('postList')
          .findIndex((p) => p.get('id') === action.postId)
        let post = state.getIn(['postList', postIndex])

        const fileIndex = post
          .get('inactiveFiles')
          .findIndex((file) => file.get('id') === action.fileId)
        const file = post.getIn(['inactiveFiles', fileIndex])

        post = post
          .set('inactiveFiles', post.get('inactiveFiles').delete(fileIndex))
          .set('files', post.get('files').push(file))

        newState = state.setIn(['postList', postIndex], post)
      }
      return newState.deleteIn(['pendingSetToActive', action.fileId])
    }
    case actionTypes.SET_MEDIA_ACTIVE_ERROR:
      return newState
        .deleteIn(['pendingSetAnactive', action.fileId])
        .deleteIn(['pendingSetToActiveErrors', action.fileId], action.error)

    case actionTypes.SHOW_INACTIVE_MEDIA:
      return newState.set('showInactiveMedia', action.status)

    case actionTypes.SET_EXTRA_PIN_PAN_TO_TOP:
      return newState.set('extraPinPanToTop', action.pan)

    case actionTypes.GET_NR_OF_OPEN_TASKS:
      return newState.set('gettingNrOfOpenTasks', true).delete('nrOfOpenTasks')
    case actionTypes.GET_NR_OF_OPEN_TASKS_DONE:
      return newState
        .set('gettingNrOfOpenTasks', false)
        .set('nrOfOpenTasks', action.openTasks)
    case actionTypes.GET_NR_OF_OPEN_TASKS_ERROR:
      return newState.set('gettingNrOfOpenTasks', false)

    case actionTypes.GET_PIN_TAGS:
      return newState.set('gettingPinTags', true).delete('pinTags')
    case actionTypes.GET_PIN_TAGS_DONE:
      return newState
        .set('gettingPinTags', false)
        .set('pinTags', action.pinTags)
    case actionTypes.GET_PIN_TAGS_ERROR:
      return newState.set('gettingPinTags', false)

    case actionTypes.GET_PIN_FIELDS:
      return newState.set('gettingPinFields', true).delete('pinFields')
    case actionTypes.GET_PIN_FIELDS_DONE:
      return newState
        .set('gettingPinFields', false)
        .set('pinFields', action.fields)
    case actionTypes.GET_PIN_FIELDS_ERROR:
      return newState.set('gettingPinFields', false)

    case actionTypes.GET_SEARCH_TAGS_SUGGESTIONS:
      return newState
        .set('gettingTagsSearchSuggestions', true)
        .delete('tagsSearchSuggestions')
    case actionTypes.GET_SEARCH_TAGS_SUGGESTIONS_DONE:
      return newState
        .set('gettingTagsSearchSuggestions', false)
        .set('tagsSearchSuggestions', action.tags)
    case actionTypes.GET_SEARCH_TAGS_SUGGESTIONS_ERROR:
      return newState.set('gettingTagsSearchSuggestions', false)

    case actionTypes.GET_TAGS_SUGGESTIONS:
      return newState
        .set('gettingTagsSuggestions', true)
        .delete('tagsSuggestions')
    case actionTypes.GET_TAGS_SUGGESTIONS_DONE:
      return newState
        .set('gettingTagsSuggestions', false)
        .set('tagsSuggestions', action.tags)
    case actionTypes.GET_TAGS_SUGGESTIONS_ERROR:
      return newState.set('gettingTagsSuggestions', false)

    case actionTypes.GET_USER_ROLE:
      return newState.set('gettingUserRole', true).delete('userRole')
    case actionTypes.GET_USER_ROLE_DONE:
      return newState.set('gettingUserRole', false).set('userRole', action.role)
    case actionTypes.GET_USER_ROLE_ERROR:
      return newState.set('gettingUserRole', false)

    case CLEAR_PROJECT_DETAILS:
      return newState.delete('assignees')

    case actionTypes.OPEN_ALERT_PLAN_VIEWER: {
      return newState.set('isOpenAlertPlanViewer', fromJS(action.modalSettings))
    }

    case LOGOUT:
      return initialState
    default:
      return state
  }
}

export default planReducer
